/**
 * Input tapes: run-length encoded InputFrames plus the course and tuning hash
 * they were recorded against. Sticks are quantized to 1/127, triggers to
 * 1/255, buttons to a bit field. A TapeBuilder authors synthetic tapes in code.
 */

import { copyInputFrame, createInputFrame, HZ, type InputFrame } from "./types";

export const TAPE_VERSION = 0;

/** [count, moveX, moveY, tiltX, tiltY, reach, pull, buttons] */
export type TapeFrame = [number, number, number, number, number, number, number, number];

export interface Tape {
  version: number;
  course: string;
  tuningHash: string;
  seed: number;
  /** Free-text note on what the tape demonstrates. */
  note?: string;
  /** Respawn point the run starts from (index into the course's respawns); absent is the course start. */
  respawn?: number;
  frames: TapeFrame[];
}

export const BTN_JUMP = 1;
export const BTN_TUCK = 2;
export const BTN_PAUSE = 4;
export const BTN_RESTART = 8;

function q127(v: number): number {
  const c = v < -1 ? -1 : v > 1 ? 1 : v;
  return Math.round(c * 127);
}

function q255(v: number): number {
  const c = v < 0 ? 0 : v > 1 ? 1 : v;
  return Math.round(c * 255);
}

export function quantizeFrame(f: InputFrame): TapeFrame {
  let b = 0;
  if (f.jump) b |= BTN_JUMP;
  if (f.tuck) b |= BTN_TUCK;
  if (f.pause) b |= BTN_PAUSE;
  if (f.restart) b |= BTN_RESTART;
  return [1, q127(f.moveX), q127(f.moveY), q127(f.tiltX), q127(f.tiltY), q255(f.reach), q255(f.pull), b];
}

/**
 * Round an InputFrame to what a tape can hold (sticks to 1/127, triggers to 1/255), in place and
 * without allocating. The live simulation steps with the rounded frame, so a recording, a replay,
 * and another player's re-simulation over the network see exactly the inputs the player's own game
 * did: an analog stick or a thumb on glass gives values a tape cannot carry, and stepping with the
 * raw ones let a replica drift a hair from the run it followed.
 */
export function quantizeInputFrame(f: InputFrame): void {
  f.moveX = q127(f.moveX) / 127;
  f.moveY = q127(f.moveY) / 127;
  f.tiltX = q127(f.tiltX) / 127;
  f.tiltY = q127(f.tiltY) / 127;
  f.reach = q255(f.reach) / 255;
  f.pull = q255(f.pull) / 255;
}

export function sameFrame(a: TapeFrame, b: TapeFrame): boolean {
  for (let i = 1; i < 8; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Decode one tape frame into an InputFrame (no allocation). */
export function decodeFrame(f: TapeFrame, out: InputFrame): void {
  out.moveX = f[1] / 127;
  out.moveY = f[2] / 127;
  out.tiltX = f[3] / 127;
  out.tiltY = f[4] / 127;
  out.reach = f[5] / 255;
  out.pull = f[6] / 255;
  out.jump = (f[7] & BTN_JUMP) !== 0;
  out.tuck = (f[7] & BTN_TUCK) !== 0;
  out.pause = (f[7] & BTN_PAUSE) !== 0;
  out.restart = (f[7] & BTN_RESTART) !== 0;
}

export function tapeLength(tape: Tape): number {
  let n = 0;
  for (const f of tape.frames) n += f[0];
  return n;
}

/** Streams the frames of a tape tick by tick without allocating. */
export class TapeReader {
  private index = 0;
  private left = 0;
  tick = 0;
  readonly total: number;

  constructor(readonly tape: Tape) {
    this.total = tapeLength(tape);
    this.left = tape.frames.length > 0 ? tape.frames[0]![0] : 0;
  }

  get done(): boolean {
    return this.tick >= this.total;
  }

  /** Write the next tick's input into `out`. Returns false when the tape is exhausted. */
  next(out: InputFrame): boolean {
    const frames = this.tape.frames;
    while (this.index < frames.length && this.left <= 0) {
      this.index++;
      this.left = this.index < frames.length ? frames[this.index]![0] : 0;
    }
    if (this.index >= frames.length) return false;
    decodeFrame(frames[this.index]!, out);
    this.left--;
    this.tick++;
    return true;
  }
}

/** Records InputFrames tick by tick into run-length encoded frames. */
export class TapeRecorder {
  frames: TapeFrame[] = [];
  private last: TapeFrame | null = null;

  constructor(
    readonly course: string,
    readonly tuningHash: string,
    readonly seed = 0,
    readonly respawn = 0,
  ) {}

  push(f: InputFrame): void {
    const q = quantizeFrame(f);
    if (this.last && sameFrame(this.last, q)) {
      this.last[0]++;
      return;
    }
    this.frames.push(q);
    this.last = q;
  }

  get ticks(): number {
    let n = 0;
    for (const f of this.frames) n += f[0];
    return n;
  }

  finish(note?: string): Tape {
    const tape: Tape = {
      version: TAPE_VERSION,
      course: this.course,
      tuningHash: this.tuningHash,
      seed: this.seed,
      frames: this.frames,
    };
    if (note) tape.note = note;
    if (this.respawn > 0) tape.respawn = this.respawn;
    return tape;
  }
}

type ButtonName = "jump" | "tuck" | "pause" | "restart";
type AxisName = "moveX" | "moveY" | "tiltX" | "tiltY" | "reach" | "pull";

/**
 * Fluent authoring of synthetic tapes:
 *   new TapeBuilder("gym", hash).hold({ moveX: 1 }).seconds(2).tap("jump").seconds(1).build()
 */
export class TapeBuilder {
  private readonly recorder: TapeRecorder;
  private readonly cur = createInputFrame();

  constructor(course: string, tuningHash: string, seed = 0) {
    this.recorder = new TapeRecorder(course, tuningHash, seed);
  }

  /** Set axes and buttons that stay in effect until changed. */
  hold(values: Partial<InputFrame>): this {
    Object.assign(this.cur, values);
    return this;
  }

  release(...names: (ButtonName | AxisName)[]): this {
    for (const n of names) {
      if (n === "jump" || n === "tuck" || n === "pause" || n === "restart") this.cur[n] = false;
      else this.cur[n] = 0;
    }
    return this;
  }

  /** Advance the given number of ticks with the current input. */
  ticks(n: number): this {
    for (let i = 0; i < n; i++) this.recorder.push(this.cur);
    return this;
  }

  seconds(s: number): this {
    return this.ticks(Math.round(s * HZ));
  }

  ms(ms: number): this {
    return this.ticks(Math.round((ms / 1000) * HZ));
  }

  /** Press a button for `ticks` ticks, then release it. */
  tap(name: ButtonName, ticks = 2): this {
    this.cur[name] = true;
    this.ticks(ticks);
    this.cur[name] = false;
    return this;
  }

  get length(): number {
    return this.recorder.ticks;
  }

  current(out: InputFrame): void {
    copyInputFrame(out, this.cur);
  }

  build(note?: string): Tape {
    return this.recorder.finish(note);
  }
}
